import { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable, Column } from '@/components/ui/data-table';
import { search, getSearchSchools } from '@/api/search';
import type { SearchResult, SearchFilters, SearchType, SchoolOption } from '@/api/search';
import {
  Search,
  User,
  Users,
  GraduationCap,
  UserCog,
  Building2,
  UserCheck,
  Mail,
  Phone,
  Hash,
  Loader2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const SEARCH_TYPES: { value: SearchType; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: 'name', label: 'Nom', icon: User },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Telephone', icon: Phone },
  { value: 'student_id', label: 'ID Élève', icon: Hash },
];

const ENTITY_TYPE_CONFIG: Record<string, { label: string; color: string; icon: React.ComponentType<{ className?: string }> }> = {
  'Eleve': { label: 'Élève', color: 'bg-blue-100 text-blue-700', icon: GraduationCap },
  'Parent 1': { label: 'Parent 1', color: 'bg-green-100 text-green-700', icon: Users },
  'Parent 2': { label: 'Parent 2', color: 'bg-emerald-100 text-emerald-700', icon: Users },
  'Enseignant': { label: 'Enseignant', color: 'bg-purple-100 text-purple-700', icon: UserCheck },
  'Direction': { label: 'Direction', color: 'bg-orange-100 text-orange-700', icon: UserCog },
  'Tiers': { label: 'Tiers', color: 'bg-gray-100 text-gray-700', icon: Building2 },
};

function getEntityBadge(type: string) {
  const config = ENTITY_TYPE_CONFIG[type] || { label: type, color: 'bg-gray-100 text-gray-700', icon: User };
  const Icon = config.icon;
  return (
    <Badge className={cn('flex items-center gap-1', config.color)}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

export function SearchPage() {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [schoolsLoading, setSchoolsLoading] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);

  // Form state
  const [searchType, setSearchType] = useState<SearchType>('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSchool, setSelectedSchool] = useState<string>('all');

  // Load schools on mount
  useEffect(() => {
    async function loadSchools() {
      try {
        const schoolsList = await getSearchSchools();
        setSchools(schoolsList);
      } catch (error) {
        console.error('Error loading schools:', error);
      } finally {
        setSchoolsLoading(false);
      }
    }
    loadSchools();
  }, []);

  // Handle search
  const handleSearch = useCallback(async () => {
    if (!searchTerm.trim()) return;

    setLoading(true);
    setHasSearched(true);
    try {
      const filters: SearchFilters = {
        type: searchType,
        term: searchTerm.trim(),
        schoolCode: selectedSchool !== 'all' ? selectedSchool : undefined,
      };
      const data = await search(filters);
      setResults(data);
    } catch (error) {
      console.error('Error searching:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [searchType, searchTerm, selectedSchool]);

  // Handle form submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch();
  };

  // Stats by type
  const statsByType = useMemo(() => {
    const stats: Record<string, number> = {};
    results.forEach((result) => {
      stats[result.type] = (stats[result.type] || 0) + 1;
    });
    return stats;
  }, [results]);

  // Table columns
  const columns: Column<SearchResult>[] = useMemo(
    () => [
      {
        key: 'school_code',
        header: 'Code école',
        sortable: true,
        render: (item) => (
          <span className="font-mono text-sm text-gray-600">{item.school_code}</span>
        ),
      },
      {
        key: 'school_name',
        header: 'École',
        sortable: true,
        render: (item) => (
          <span className="font-medium text-gray-900">{item.school_name}</span>
        ),
      },
      {
        key: 'type',
        header: 'Type',
        sortable: true,
        render: (item) => getEntityBadge(item.type),
      },
      {
        key: 'id',
        header: 'ID',
        sortable: true,
        render: (item) => (
          <span className="font-mono text-sm text-gray-600">{item.id}</span>
        ),
      },
      {
        key: 'name',
        header: 'Nom',
        sortable: true,
        render: (item) => (
          <span className="font-medium text-gray-900">{item.name}</span>
        ),
      },
      {
        key: 'email',
        header: 'Email',
        sortable: true,
        render: (item) => (
          <span className="text-sm text-gray-600">{item.email || '-'}</span>
        ),
      },
      {
        key: 'phone',
        header: 'Telephone',
        sortable: true,
        render: (item) => (
          <span className="text-sm text-gray-600">{item.phone || '-'}</span>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Search className="h-5 w-5" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
            Recherches
          </h1>
        </div>
        <p className="text-sm md:text-base text-muted-foreground pl-[52px]">
          Recherchez des utilisateurs dans toutes les écoles
        </p>
      </div>

      {/* Search Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Criteres de recherche</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search Type */}
              <div className="space-y-2">
                <Label htmlFor="searchType">Type de recherche</Label>
                <Select
                  value={searchType}
                  onValueChange={(value) => setSearchType(value as SearchType)}
                >
                  <SelectTrigger id="searchType">
                    <SelectValue placeholder="Selectionner un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {SEARCH_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="h-4 w-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Search Term */}
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="searchTerm">Terme de recherche</Label>
                <Input
                  id="searchTerm"
                  type="text"
                  placeholder={
                    searchType === 'email'
                      ? 'exemple@email.com'
                      : searchType === 'phone'
                      ? '0612345678'
                      : searchType === 'student_id'
                      ? "ID de l'élève"
                      : 'Nom Prenom'
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* School Filter */}
              <div className="space-y-2">
                <Label htmlFor="school">École (optionnel)</Label>
                <Select
                  value={selectedSchool}
                  onValueChange={setSelectedSchool}
                  disabled={schoolsLoading}
                >
                  <SelectTrigger id="school">
                    <SelectValue placeholder="Toutes les écoles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les écoles</SelectItem>
                    {schools.map((school) => (
                      <SelectItem key={school.code} value={school.code}>
                        {school.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={loading || !searchTerm.trim()}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Recherche en cours...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Rechercher
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Results Stats */}
      {hasSearched && !loading && results.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
          {Object.entries(ENTITY_TYPE_CONFIG).map(([type, config]) => {
            const count = statsByType[type] || 0;
            const Icon = config.icon;
            return (
              <Card key={type} className="p-3">
                <div className="flex items-center gap-2">
                  <div className={cn('rounded-lg p-2', config.color)}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-lg font-bold">{count}</p>
                    <p className="text-xs text-gray-500">{config.label}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Results Table */}
      {hasSearched && (
        <DataTable
          columns={columns}
          data={results}
          loading={loading}
          keyExtractor={(item, index) => `${item.school_code}-${item.type}-${item.id}-${index}`}
          emptyMessage="Aucun resultat trouve"
        />
      )}

      {/* Initial State */}
      {!hasSearched && (
        <Card className="p-12">
          <div className="text-center text-gray-500">
            <Search className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <p className="text-lg font-medium">Lancez une recherche</p>
            <p className="text-sm">
              Utilisez le formulaire ci-dessus pour rechercher des utilisateurs
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}
